import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../AxiosInstance';
import { Toast, SearchBar, LoadingSpinner, EmptyState } from './v2-utils';
import '../styles/v2.css';

/* ── helpers (ported from ViewInventory.js) ── */
const getSuppliersForUnit = (item, unitId) => {
  if (!unitId) return [];
  const map = item.unitSuppliersByUnitId || {};
  const s = map[unitId];
  return Array.isArray(s) ? s : [];
};

const getDefaultSupplierIdForUnit = (item, unitId) => {
  const suppliers = getSuppliersForUnit(item, unitId);
  if (!suppliers.length) return item.supplierId != null ? String(item.supplierId) : '';
  const primary = suppliers.find(s => s?.isPrimary);
  if (primary?.supplierId != null) return String(primary.supplierId);
  const existing = item.supplierId != null ? String(item.supplierId) : null;
  if (existing && suppliers.some(s => String(s.supplierId) === existing)) return existing;
  const first = suppliers[0];
  return first?.supplierId != null ? String(first.supplierId) : '';
};

const getEffectiveSupplier = (item, key, unitId, selectedSuppliers) => {
  const suppliers = getSuppliersForUnit(item, unitId);
  if (suppliers.length > 0) {
    const selId = selectedSuppliers[key] || getDefaultSupplierIdForUnit(item, unitId);
    const found = suppliers.find(s => String(s.supplierId) === String(selId));
    if (found) return found;
    return suppliers[0];
  }
  return { supplierId: item.supplierId ?? null, supplierName: item.supplierName || 'Supplier', supplierPhoneNumber: item.supplierPhoneNumber || '' };
};

const makeKey = item => `${item.inventoryId}-${item.productId}-${item.supplierId}`;

function formatProductName(item) {
  let s = item.productName || '';
  if (item.brand || item.variety) s += ` (${[item.brand, item.variety].filter(Boolean).join(' / ')})`;
  if (item.size) s += ` [${item.size}]`;
  return s;
}

export default function ReorderInventoryV2() {
  const [candidates, setCandidates]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedMap, setSelectedMap]       = useState({});
  const [selectedUnits, setSelectedUnits]   = useState({});
  const [reorderQtys, setReorderQtys]       = useState({});
  const [selectedSuppliers, setSelectedSuppliers] = useState({});
  const [messages, setMessages]             = useState([]);
  const [msgIndex, setMsgIndex]             = useState(0);
  const [generatedText, setGeneratedText]   = useState('');
  const [search, setSearch]                 = useState('');
  const [toast, setToast]                   = useState(null);
  const [generating, setGenerating]         = useState(false);
  const [isDesktop, setIsDesktop]           = useState(window.innerWidth >= 900);
  const messagesRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axiosInstance.get('/inventories/reorder-candidates')
      .then(res => {
        const items = res.data?.reorderCandidates || [];
        const initSel={}, initUnits={}, initQtys={}, initSups={};
        items.forEach(item => {
          const key = makeKey(item);
          initSel[key] = !!item.isReorderCandidate;
          const defUnit = item.defaultUnitId || item.unitTypes?.[0]?.unitId || '';
          initUnits[key] = defUnit;
          let qty = 0;
          if (defUnit && Array.isArray(item.unitTypes)) {
            const u = item.unitTypes.find(u => u.unitId === defUnit);
            if (u?.reorderQuantity > 0) qty = u.reorderQuantity;
          }
          if (!qty && item.reorderQuantity > 0) qty = item.reorderQuantity;
          initQtys[key] = qty > 0 ? String(qty) : '';
          initSups[key] = getDefaultSupplierIdForUnit(item, defUnit);
        });
        setCandidates(items);
        setSelectedMap(initSel); setSelectedUnits(initUnits); setReorderQtys(initQtys); setSelectedSuppliers(initSups);
      })
      .catch(() => setToast({ message: 'Failed to load reorder candidates.', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const filtered = candidates.filter(item => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${item.productName||''} ${item.brand||''} ${item.variety||''}`.toLowerCase().includes(q);
  });

  const allSelected = filtered.length > 0 && filtered.every(item => !!selectedMap[makeKey(item)]);
  const someSelected = filtered.some(item => !!selectedMap[makeKey(item)]);

  const toggleAll = () => setSelectedMap(prev => {
    const next = {...prev};
    filtered.forEach(item => { next[makeKey(item)] = !allSelected; });
    return next;
  });

  const handleUnitChange = (item, key, unitId) => {
    setSelectedUnits(p => ({...p,[key]:unitId}));
    let qty = 0;
    if (Array.isArray(item.unitTypes)) {
      const u = item.unitTypes.find(u => u.unitId === unitId);
      if (u?.reorderQuantity > 0) qty = u.reorderQuantity;
    }
    setReorderQtys(p => ({...p,[key]:qty>0?String(qty):''}));
    setSelectedSuppliers(p => ({...p,[key]:getDefaultSupplierIdForUnit(item, unitId)}));
  };

  const handleGenerate = async () => {
    const selected = candidates.filter(item => selectedMap[makeKey(item)]);
    if (!selected.length) { setToast({ message:'Select at least one item.', severity:'error' }); return; }

    setGenerating(true);

    const reorders = selected.map(item => {
      const key = makeKey(item);
      const unitId = selectedUnits[key] || item.defaultUnitId || null;
      const sup = getEffectiveSupplier(item, key, unitId, selectedSuppliers);
      const qty = Number(reorderQtys[key] || 0);
      return { productId: item.productId, supplierId: sup.supplierId != null ? Number(sup.supplierId) : null, unitId, orderQuantity: qty };
    });

    try {
      await axiosInstance.post('/inventories/reorders/bulk-upsert', { reorders });
    } catch {
      setToast({ message:'Failed to save reorder records.', severity:'error' });
      setGenerating(false); return;
    }

    /* group by supplier */
    const grouped = new Map();
    selected.forEach(item => {
      const key = makeKey(item);
      const unitId = selectedUnits[key] || item.defaultUnitId || null;
      const sup = getEffectiveSupplier(item, key, unitId, selectedSuppliers);
      const supKey = sup.supplierId != null ? String(sup.supplierId) : `no-${sup.supplierName||'Supplier'}`;
      const unit = (item.unitTypes||[]).find(u => u.unitId===unitId) || item.unitTypes?.[0];
      const brandVariety = [item.brand,item.variety].filter(Boolean).join(' / ');
      const detail = [brandVariety,item.size].filter(Boolean).join(' / ');
      const qty = Number(reorderQtys[key]||0);

      if (!grouped.has(supKey)) grouped.set(supKey, { supplierName: sup.supplierName||'Supplier', phone: sup.supplierPhoneNumber||'', rows:[], firstKey: key });
      grouped.get(supKey).rows.push({ productName:item.productName, detail, qty, unit:unit?.unitType||'' });
    });

    const newMsgs = [];
    grouped.forEach(({supplierName,phone,rows,firstKey}) => {
      if (!rows.length) return;
      const itemsText = rows.map(r=>`- ${r.productName}${r.detail?` (${r.detail})`:''}: ${r.qty} ${r.unit}`).join('\n');
      const msg = `Dear ${supplierName} :\n\nI need to reorder the following:\n\n${itemsText}\n\nThank you!`;
      newMsgs.push({ key:firstKey, message:msg, phone });
    });

    if (!newMsgs.length) { setToast({message:'Nothing to generate.',severity:'error'}); setGenerating(false); return; }
    setMessages(newMsgs); setMsgIndex(0); setGeneratedText(newMsgs[0].message);
    setToast({ message:`Generated ${newMsgs.length} supplier message${newMsgs.length!==1?'s':''}.`, severity:'success' });
    setGenerating(false);
    setTimeout(() => messagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleWhatsApp = () => {
    const current = messages[msgIndex];
    if (!current) { setToast({message:'Generate a message first.',severity:'error'}); return; }
    if (!current.phone) { setToast({message:'No phone number for this supplier. Add one in Suppliers.',severity:'error'}); return; }
    const phone = String(current.phone).replace(/[^0-9]/g,'');
    if (!phone) { setToast({message:'Invalid phone number.',severity:'error'}); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(current.message)}`, '_blank');
  };

  const handleCopy = async () => {
    const current = messages[msgIndex];
    const text = current?.message || generatedText;
    if (!text) { setToast({message:'Nothing to copy.',severity:'error'}); return; }
    try {
      await navigator.clipboard.writeText(text);
      setToast({message:'Copied to clipboard.',severity:'success'});
    } catch { setToast({message:'Failed to copy.',severity:'error'}); }
  };

  const switchMsg = (newIdx) => {
    if (newIdx < 0 || newIdx >= messages.length) return;
    setMsgIndex(newIdx); setGeneratedText(messages[newIdx].message);
  };

  if (loading) return <LoadingSpinner message="Loading reorder candidates…" />;

  const selectedCount = Object.values(selectedMap).filter(Boolean).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ color:'#0f172a', fontSize:20, fontWeight:800, margin:0, letterSpacing:'-0.02em' }}>Reorder Inventory</h1>
          <p style={{ color:'#94a3b8', fontSize:13, margin:'4px 0 0' }}>{filtered.length} item{filtered.length!==1?'s':''} below threshold · {selectedCount} selected</p>
        </div>
        <button onClick={handleGenerate} disabled={!someSelected||generating} className="v2-btn-submit"
          style={{ width:'auto', padding:'10px 20px', fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
          <svg style={{ width:15,height:15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
          {generating ? 'Generating…' : 'Generate Reorder List'}
        </button>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Filter by product name, brand, variety…" />

      {filtered.length === 0 ? (
        <EmptyState message={search ? 'No items match your filter.' : 'No items below stock threshold. Great job!'} />
      ) : isDesktop ? (
        /* ── Desktop: table ── */
        <div className="v2-card" style={{ overflow:'hidden' }}>
          <table className="v2-table" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #f1f5f1' }}>
                <th style={{ padding:'12px 16px', width:40 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor:'#10b981', width:16, height:16, cursor:'pointer' }} />
                </th>
                {['Product','Stock','Unit','Supplier','Qty'].map(h=>(
                  <th key={h} style={{ padding:'12px 16px', textAlign:h==='Product'?'left':'center', fontSize:10.5, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const key = makeKey(item);
                const unitId = selectedUnits[key] || '';
                const suppliersForUnit = getSuppliersForUnit(item, unitId);
                const allSuppliers = item.unitTypes?.flatMap(u => getSuppliersForUnit(item, u.unitId)||[]).filter((s,idx,arr)=>s&&arr.findIndex(x=>String(x.supplierId)===String(s.supplierId))===idx) || [];
                const isCandidate = item.isReorderCandidate;
                return (
                  <tr key={key} style={{ borderBottom:i<filtered.length-1?'1px solid #f8faf8':'none', background:selectedMap[key]?'#f0fdf4':'white' }}>
                    <td style={{ padding:'12px 16px' }}>
                      <input type="checkbox" checked={!!selectedMap[key]} onChange={()=>setSelectedMap(p=>({...p,[key]:!p[key]}))} style={{ accentColor:'#10b981', width:16, height:16, cursor:'pointer' }} />
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {isCandidate && <div className="v2-pulse" style={{ width:8,height:8,borderRadius:'50%',background:'#ef4444',flexShrink:0 }} />}
                        <div>
                          <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#1e293b', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{formatProductName(item)}</p>
                          {isCandidate && <p style={{ margin:0, fontSize:10, color:'#ef4444', fontWeight:600 }}>Below threshold</p>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px', textAlign:'center', fontSize:13, fontWeight:600, color:isCandidate?'#ef4444':'#334155' }}>
                      {item.currentStock != null ? Number(item.currentStock).toFixed(2) : '—'}
                    </td>
                    <td style={{ padding:'12px 16px', textAlign:'center' }}>
                      <div style={{ position:'relative' }}>
                        <select className="v2-input" style={{ paddingRight:24, appearance:'none', cursor:'pointer', fontSize:12, textAlign:'center' }}
                          value={unitId} onChange={e=>handleUnitChange(item,key,e.target.value)}>
                          {(item.unitTypes||[]).map(u=><option key={u.unitId} value={u.unitId}>{u.unitType}</option>)}
                        </select>
                        <svg style={{ position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',width:12,height:12,color:'#94a3b8',pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px', textAlign:'center' }}>
                      <div style={{ position:'relative' }}>
                        <select className="v2-input" style={{ paddingRight:24, appearance:'none', cursor:'pointer', fontSize:12 }}
                          value={selectedSuppliers[key]||''} onChange={e=>setSelectedSuppliers(p=>({...p,[key]:e.target.value}))} disabled={!suppliersForUnit.length&&!allSuppliers.length}>
                          {(suppliersForUnit.length?suppliersForUnit:allSuppliers).map(s=>(
                            <option key={s.supplierId} value={String(s.supplierId)}>{s.supplierName}{s.isPrimary?' (primary)':''}</option>
                          ))}
                          {!suppliersForUnit.length&&!allSuppliers.length&&<option value="">No supplier</option>}
                        </select>
                        <svg style={{ position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',width:12,height:12,color:'#94a3b8',pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px', textAlign:'center' }}>
                      <input className="v2-input" type="number" placeholder="Qty"
                        style={{ width:70, textAlign:'center', fontSize:13, padding:'8px 10px' }}
                        value={reorderQtys[key]||''} onChange={e=>setReorderQtys(p=>({...p,[key]:e.target.value}))} min="0" step="1" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* ── Mobile: cards ── */
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderRadius:10, background:'#f8fafc', border:'1px solid #e2e8f0' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor:'#10b981', width:16, height:16 }} />
            <span style={{ fontSize:13, fontWeight:600, color:'#475569' }}>Select all ({filtered.length})</span>
          </div>
          {filtered.map(item => {
            const key=makeKey(item), unitId=selectedUnits[key]||'';
            const suppliersForUnit=getSuppliersForUnit(item,unitId);
            const allSup=item.unitTypes?.flatMap(u=>getSuppliersForUnit(item,u.unitId)||[]).filter((s,i,a)=>s&&a.findIndex(x=>String(x.supplierId)===String(s.supplierId))===i)||[];
            const isCandidate=item.isReorderCandidate;
            return (
              <div key={key} className="v2-card" style={{ padding:18, overflow:'hidden', background:selectedMap[key]?'#f0fdf4':'white', border:selectedMap[key]?'1.5px solid #a7f3d0':'1.5px solid transparent' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                  <input type="checkbox" checked={!!selectedMap[key]} onChange={()=>setSelectedMap(p=>({...p,[key]:!p[key]}))} style={{ accentColor:'#10b981', width:18, height:18, cursor:'pointer', marginTop:2 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{formatProductName(item)}</p>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                      {isCandidate&&<span style={{ fontSize:10, fontWeight:700, color:'#ef4444', background:'#fef2f2', padding:'2px 6px', borderRadius:99 }}>Below threshold</span>}
                      <span style={{ fontSize:12, color:'#64748b' }}>Stock: {item.currentStock!=null?Number(item.currentStock).toFixed(2):'—'}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div style={{ position:'relative' }}>
                    <select className="v2-input" style={{ paddingRight:24, appearance:'none', fontSize:12 }}
                      value={unitId} onChange={e=>handleUnitChange(item,key,e.target.value)}>
                      {(item.unitTypes||[]).map(u=><option key={u.unitId} value={u.unitId}>{u.unitType}</option>)}
                    </select>
                    <svg style={{ position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',width:12,height:12,color:'#94a3b8',pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                  </div>
                  <input className="v2-input" type="number" placeholder="Qty" style={{ fontSize:13, padding:'8px 10px' }}
                    value={reorderQtys[key]||''} onChange={e=>setReorderQtys(p=>({...p,[key]:e.target.value}))} min="0" step="1" />
                  <div style={{ position:'relative', gridColumn:'1/-1' }}>
                    <select className="v2-input" style={{ paddingRight:24, appearance:'none', fontSize:12 }}
                      value={selectedSuppliers[key]||''} onChange={e=>setSelectedSuppliers(p=>({...p,[key]:e.target.value}))} disabled={!suppliersForUnit.length&&!allSup.length}>
                      {(suppliersForUnit.length?suppliersForUnit:allSup).map(s=><option key={s.supplierId} value={String(s.supplierId)}>{s.supplierName}{s.isPrimary?' (primary)':''}</option>)}
                      {!suppliersForUnit.length&&!allSup.length&&<option value="">No supplier linked</option>}
                    </select>
                    <svg style={{ position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',width:12,height:12,color:'#94a3b8',pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Generated messages panel */}
      {messages.length > 0 && (
        <div ref={messagesRef} className="v2-card" style={{ padding:24, display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
            <div>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:'#1e293b' }}>Reorder Message</h3>
              <p style={{ margin:'4px 0 0', fontSize:12, color:'#94a3b8' }}>Message {msgIndex+1} of {messages.length} — {messages[msgIndex]?.phone ? `Phone: ${messages[msgIndex].phone}` : 'No phone number'}</p>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {messages.length > 1 && (
                <>
                  <button onClick={()=>switchMsg(msgIndex-1)} disabled={msgIndex===0}
                    style={{ width:34,height:34,borderRadius:9,border:'1px solid #e2e8f0',background:'white',cursor:msgIndex===0?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:msgIndex===0?'#cbd5e1':'#475569' }}>
                    <svg style={{ width:14,height:14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                  </button>
                  <button onClick={()=>switchMsg(msgIndex+1)} disabled={msgIndex===messages.length-1}
                    style={{ width:34,height:34,borderRadius:9,border:'1px solid #e2e8f0',background:'white',cursor:msgIndex===messages.length-1?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:msgIndex===messages.length-1?'#cbd5e1':'#475569' }}>
                    <svg style={{ width:14,height:14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </button>
                </>
              )}
              <button onClick={handleCopy}
                style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:9,border:'1.5px solid #e2e8f0',background:'white',color:'#475569',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                <svg style={{ width:14,height:14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" /></svg>
                Copy
              </button>
              <button onClick={handleWhatsApp}
                style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:9,border:'none',background:'#25D366',color:'white',fontSize:12,fontWeight:700,cursor:'pointer' }}>
                <svg style={{ width:14,height:14 }} fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
            </div>
          </div>
          <textarea value={generatedText} onChange={e=>setGeneratedText(e.target.value)} rows={8}
            style={{ width:'100%', padding:'14px 16px', borderRadius:12, border:'1.5px solid #e2e8f0', fontSize:13, fontFamily:'Inter,system-ui,sans-serif', lineHeight:1.7, color:'#334155', resize:'vertical', outline:'none', boxSizing:'border-box' }}
            onFocus={e=>e.target.style.borderColor='#10b981'}
            onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
        </div>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
